import { X } from 'lucide-react';
import { CSSProperties, ReactNode, useCallback } from 'react';

import { Avatar, AvatarFallback, Button, Sheet, SheetContent, Text } from '@/design-system/components/ui';
import { useConfirm } from '@/hooks/useConfirm';

interface ConfirmCloseOptions {
  title?: string;
  message?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

interface Props {
  children: ReactNode;
  disableClickOutside?: boolean;
  icon?: ReactNode;
  onClickSecondaryIcon?: () => void;
  onClose: () => void;
  open: boolean;
  secondaryIcon?: ReactNode;
  subtitle?: ReactNode;
  title: string;
  width?: string;
  disableAutoFocus?: boolean;
  disableEnforceFocus?: boolean;
  disableRestoreFocus?: boolean;
  /** @deprecated drawerSx is no longer applied — use className on the wrapper instead */
  drawerSx?: Record<string, unknown>;
  iconColor?: string;
  textColor?: string;
  contentStyle?: CSSProperties;
  contentClassName?: string;
  /**
   * When true, any dismiss attempt (outside click, escape key, or the header X button)
   * shows a confirmation dialog before actually closing. When the user confirms,
   * `onClose` is called; when they cancel, the drawer stays open.
   *
   * Supersedes `disableClickOutside` — when `shouldConfirmClose` is true, escape key
   * and outside click are both intercepted regardless of `disableClickOutside`.
   */
  shouldConfirmClose?: boolean;
  /** Optional overrides for the confirmation dialog copy. */
  confirmCloseOptions?: ConfirmCloseOptions;
}

const DEFAULT_CONFIRM_CLOSE_OPTIONS: Required<ConfirmCloseOptions> = {
  title: 'Discard unsaved changes?',
  message: 'Your changes will be lost.',
  confirmButtonText: 'Discard',
  cancelButtonText: 'Keep editing',
};

const TemporaryDrawer = ({
  open,
  onClickSecondaryIcon,
  onClose,
  children,
  title,
  subtitle,
  icon,
  secondaryIcon,
  width,
  disableClickOutside = false,
  iconColor,
  textColor,
  contentStyle,
  contentClassName,
  shouldConfirmClose = false,
  confirmCloseOptions,
}: Props) => {
  const [ConfirmDialog, confirm] = useConfirm('warning');

  const requestClose = useCallback(async () => {
    if (!shouldConfirmClose) {
      onClose();
      return;
    }
    const ok = await confirm({
      title: confirmCloseOptions?.title ?? DEFAULT_CONFIRM_CLOSE_OPTIONS.title,
      message: confirmCloseOptions?.message ?? DEFAULT_CONFIRM_CLOSE_OPTIONS.message,
      confirmButtonText: confirmCloseOptions?.confirmButtonText ?? DEFAULT_CONFIRM_CLOSE_OPTIONS.confirmButtonText,
      cancelButtonText: confirmCloseOptions?.cancelButtonText ?? DEFAULT_CONFIRM_CLOSE_OPTIONS.cancelButtonText,
    });
    if (ok) onClose();
  }, [shouldConfirmClose, onClose, confirm, confirmCloseOptions]);

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(isOpen) => {
          if (isOpen) return;
          if (shouldConfirmClose || disableClickOutside) return;
          onClose();
        }}
      >
        <SheetContent
          side="right"
          hideCloseButton
          className={`tw:overflow-y-auto tw:p-6 tw:md:p-12 tw:max-w-[42rem] tw:w-full tw:md:w-[55%] tw:z-[1201] ${contentClassName || ''}`}
          style={{ ...(width ? { width } : {}), ...contentStyle }}
          onInteractOutside={(e) => {
            if (shouldConfirmClose) {
              e.preventDefault();
              void requestClose();
              return;
            }
            if (disableClickOutside) {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e) => {
            if (shouldConfirmClose) {
              e.preventDefault();
              void requestClose();
            }
          }}
        >
          <div className="tw:flex tw:flex-col tw:gap-8">
            <div className="tw:flex tw:flex-row tw:justify-between tw:items-start">
              <div className={`tw:flex tw:flex-row tw:gap-3 ${subtitle ? 'tw:items-start' : 'tw:items-center'}`}>
                {icon && (
                  <Avatar size="lg">
                    <AvatarFallback>{icon}</AvatarFallback>
                  </Avatar>
                )}
                <div className="tw:flex tw:flex-col tw:gap-1">
                  <Text variant="title-sm" as="h2" style={textColor ? { color: textColor } : undefined}>
                    {title}
                  </Text>
                  {subtitle && (
                    <Text
                      variant="body-sm"
                      color="muted"
                      as="span"
                      style={textColor ? { color: textColor, opacity: 0.8 } : undefined}
                    >
                      {subtitle}
                    </Text>
                  )}
                </div>
              </div>
              <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
                {secondaryIcon && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClickSecondaryIcon}
                    style={iconColor ? { color: iconColor } : undefined}
                  >
                    {secondaryIcon}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => void requestClose()}
                  style={iconColor ? { color: iconColor } : undefined}
                >
                  <X />
                </Button>
              </div>
            </div>
            <div>{children}</div>
          </div>
        </SheetContent>
      </Sheet>
      {ConfirmDialog}
    </>
  );
};

export default TemporaryDrawer;
